import {
  useState,
  useEffect } from "react";
import { useParams,
  useNavigate } from "react-router-dom";
import {
  getProductDetailPath,
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ProductCard
} from "@b2b/ui-components";
import {
  Search,
  Package,
  SlidersHorizontal,
  Filter,
  TrendingUp,
  ChevronLeft,
  Building2,
  MapPin,
} from "lucide-react";
import { productService, adminService } from "@b2b/services";
import { mapProductsToCardFormat } from "../../utils/productMapper";
import { useAuth } from "../../auth/Authprovider";
import { useChatUnreadHeader } from "../../chat/ChatUnreadHeaderProvider";

function isProductActive(product: any): boolean {
  const status = String(product?.status ?? product?.productStatus ?? "").toLowerCase();
  if (status) {
    return ["active", "approved", "published", "live"].includes(status);
  }
  if (typeof product?.isActive === "boolean") return product.isActive;
  if (typeof product?.active === "boolean") return product.active;
  return true;
}

const ProductList = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const chatUnread = useChatUnreadHeader();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("relevance");
  const [products, setProducts] = useState<any[]>([]);
  const [rawProducts, setRawProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<"products" | "suppliers">("products");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  useEffect(() => {
    if (!categoryId) return;

    let active = true;
    setLoading(true);
    setError(null);

    // Fetch category info
    adminService
      .getCategories(true)
      .then((categories) => {
        if (!active) return;
        const all = Array.isArray(categories) ? categories : [];
        const found = all.find(
          (cat: any) => cat.id === categoryId || String(cat.id) === categoryId
        );
        setCategory(found || null);
      })
      .catch((err) => {
        console.error("Error fetching category:", err);
      });

    // Fetch products
    productService
      .getProductsByCategory(categoryId, {
        page: pagination.page,
        limit: pagination.limit,
      })
      .then((response) => {
        if (!active) return;
        console.log("Products API response:", response);
        // Response structure: { success: true, data: { products: [...], pagination: {...} }, timestamp: ... }
        if (response && response.success) {
          // Products are in response.data.products
          const productsList = response.data?.products || [];
          console.log("Products count:", productsList.length);
          console.log("Category ID:", categoryId);

          if (productsList.length === 0) {
            console.warn("No products returned for category:", categoryId);
          }

          // Map products to ProductCard format using shared utility
          const mappedProducts = mapProductsToCardFormat(productsList);
          setProducts(mappedProducts);
          setRawProducts(productsList);

          // Pagination is in response.data.pagination
          const paginationData = response.data?.pagination;
          if (paginationData) {
            setPagination(paginationData);
          }
        } else {
          console.error("Invalid response structure:", response);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error fetching products:", err);
        console.error("Error details:", {
          message: err?.message,
          response: err?.response?.data,
          categoryId,
        });
        setError(`Failed to load products: ${err?.message || "Unknown error"}`);
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [categoryId, pagination.page, pagination.limit]);

  const filteredProducts = products.filter(
    (product) =>
      searchQuery === "" ||
      product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.supplier.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const suppliers = rawProducts.reduce((acc: any[], product: any) => {
    const supplierSource = product.vendor || product.company || product.assignedSeller;
    if (!supplierSource) return acc;

    const supplierId =
      supplierSource.id ||
      supplierSource.userId ||
      supplierSource.companyId ||
      supplierSource.name;
    if (!supplierId) return acc;

    const existing = acc.find((s) => s.id === String(supplierId));
    const normalizedProduct = {
      id: product.id,
      name: product.name,
      image:
        product.thumbnailImage ||
        product.images?.[0]?.url ||
        product.images?.[0] ||
        "",
    };

    if (existing) {
      existing.products.push(normalizedProduct);
      return acc;
    }

    acc.push({
      id: String(supplierId),
      name: supplierSource.name || supplierSource.companyName || "Supplier",
      country: supplierSource.country || supplierSource.countryName || "Global",
      city: supplierSource.city || supplierSource.location || "",
      verified: true,
      products: [normalizedProduct],
    });
    return acc;
  }, []);

  const activeProductCount = rawProducts.reduce(
    (sum, product) => sum + (isProductActive(product) ? 1 : 0),
    0
  );

  const goToProduct = (productId: string) => {
    navigate(getProductDetailPath(productId));
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <BizcoverIndiaFigmaHeader />

      {/* Page Header */}
      <div className="border-b border-gray-200 py-6 sm:py-8">
        <div className="mx-auto w-full max-w-[90rem] px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="text-gray-600 hover:text-orange-600"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              </div>
              <h1 className="text-[clamp(1.5rem,2vw+0.5rem,2rem)] font-bold text-gray-900 mb-1">
                {category?.name || "Products"}
              </h1>
              <p className="text-[clamp(0.875rem,1vw+0.5rem,1rem)] text-gray-600">
                {activeProductCount} active products from
                verified suppliers
              </p>
            </div>
            <Badge className="text-xs sm:text-sm flex items-center gap-1">
              <Package className="h-3 w-3" />
              {category?.name || "Category"}
            </Badge>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="mx-auto w-full max-w-[90rem] px-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={() => setActiveTab("products")}
              className={`py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === "products"
                  ? "border-orange-500 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              Products ({filteredProducts.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("suppliers")}
              className={`py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === "suppliers"
                  ? "border-orange-500 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              Suppliers ({suppliers.length})
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 py-4 sm:py-6">
        <div className="mx-auto w-full max-w-[90rem] px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-stretch md:items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 w-full max-w-full md:max-w-lg lg:max-w-xl">
              <Input
                placeholder="Search products, suppliers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>

            {/* Controls */}
            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto justify-between md:justify-end">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 sm:flex-none"
              >
                <SlidersHorizontal className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Price Range</span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="flex-1 sm:flex-none"
              >
                <Filter className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Location</span>
              </Button>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px] sm:w-[180px]">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="relevance">Relevance</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="moq">Min Order Quantity</SelectItem>
                  <SelectItem value="rating">Supplier Rating</SelectItem>
                  <SelectItem value="newest">Newest First</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto w-full max-w-[90rem] px-3 sm:px-6 py-8">
        {/* Results Info */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-2">
          <p className="text-[clamp(0.875rem,1vw+0.5rem,1rem)] text-gray-500">
              Showing{" "}
              {activeTab === "products" ? filteredProducts.length : suppliers.length}{" "}
              results
          </p>
          <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
            <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
            <span className="text-gray-500">Updated 2 hours ago</span>
          </div>
        </div>

        {/* Products / Suppliers Content */}
        {loading ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">Loading products...</p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-red-600 text-lg">{error}</p>
          </div>
        ) : activeTab === "products" && filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">
              No products found matching your search.
            </p>
          </div>
        ) : activeTab === "products" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
            {filteredProducts.map((product, index) => (
              <div
                key={`${product.id}-${index}`}
                role="group"
                tabIndex={0}
                aria-label={`${product.title}. View product.`}
                className="cursor-pointer rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-[#F97316] focus-visible:ring-offset-2"
                onClick={(e) => {
                  if (
                    (e.target as HTMLElement).closest(
                      "button, a, [role='button']",
                    )
                  ) {
                    return;
                  }
                  goToProduct(String(product.id));
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  if (
                    (e.target as HTMLElement).closest(
                      "button, a, [role='button']",
                    )
                  ) {
                    return;
                  }
                  e.preventDefault();
                  goToProduct(String(product.id));
                }}
              >
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">
              No suppliers found for this category.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {suppliers.map((supplier: any) => (
              <div
                key={supplier.id}
                className="rounded-xl border border-gray-200 bg-white p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-base font-semibold text-gray-900 line-clamp-1">
                      {supplier.name}
                    </p>
                    <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                      <MapPin className="h-3.5 w-3.5" />
                      <span>
                        {supplier.city ? `${supplier.city}, ` : ""}
                        {supplier.country}
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-700 border border-green-200">
                    Verified
                  </Badge>
                </div>

                <div className="mt-4 border-t border-gray-100 pt-3">
                  <p className="text-xs text-gray-500 mb-2">
                    Related category products ({supplier.products.length})
                  </p>
                  <div className="flex gap-2">
                    {supplier.products.slice(0, 3).map((p: any) => (
                      <div
                        key={p.id}
                        className="w-20 h-16 rounded border border-gray-200 bg-gray-100 overflow-hidden flex items-center justify-center"
                      >
                        {p.image ? (
                          <img
                            src={p.image}
                            alt={p.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <Building2 className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {activeTab === "products" && pagination.totalPages > 1 && (
          <div className="flex justify-center mt-10 sm:mt-12">
            <div className="flex flex-wrap items-center gap-1 sm:gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!pagination.hasPrev}
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page - 1 })
                }
              >
                Previous
              </Button>
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                .slice(
                  Math.max(0, pagination.page - 3),
                  Math.min(pagination.totalPages, pagination.page + 2)
                )
                .map((page) => (
                  <Button
                    key={page}
                    variant={page === pagination.page ? "default" : "outline"}
                    size="sm"
                    className="w-8 sm:w-10"
                    onClick={() => setPagination({ ...pagination, page })}
                  >
                    {page}
                  </Button>
                ))}
              <Button
                variant="outline"
                size="sm"
                disabled={!pagination.hasNext}
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page + 1 })
                }
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <BizcoverIndiaFigmaFooter />
    </div>
  );
};

export default ProductList;
